import type { DayKey, GameEvent, QuestProgress } from "./types";

/**
 * Quests/misiones: objetivos con periodo (diario/semanal/único). Un objetivo
 * acumula por una MÉTRICA (tipo de evento o meta). La quest se completa cuando
 * todos sus objetivos llegan a su `target`. Puro.
 */

export type QuestPeriod = "daily" | "weekly" | "once";

export interface QuestGoal {
  /** Clave de métrica: normalmente el `type` del evento. */
  metric: string;
  target: number;
  /** Cuánto suma cada evento: 1 (conteo) o el `value` del evento. */
  useValue?: boolean;
}

export interface QuestDef {
  id: string;
  title: string;
  period: QuestPeriod;
  goals: QuestGoal[];
  bonusXp?: number;
}

/** Clave de periodo para reseteo (diario = día; semanal = lunes ISO; once = fijo). */
export function periodKey(period: QuestPeriod, day: DayKey): string {
  if (period === "once") {
    return "once";
  }
  if (period === "daily") {
    return day;
  }
  // Semanal: ancla al lunes ISO.
  const d = new Date(`${day}T00:00:00Z`);
  const dow = (d.getUTCDay() + 6) % 7; // lunes=0
  d.setUTCDate(d.getUTCDate() - dow);
  return `w:${d.toISOString().slice(0, 10)}`;
}

function emptyProgress(pk: string): QuestProgress {
  return { progress: {}, completed: false, periodKey: pk };
}

/**
 * Aplica un evento a una quest y devuelve el progreso nuevo (no muta). Si el
 * periodo cambió respecto al progreso previo, resetea antes de aplicar.
 */
export function applyEventToQuest(
  def: QuestDef,
  prev: QuestProgress | undefined,
  event: GameEvent,
  day: DayKey,
): QuestProgress {
  const pk = periodKey(def.period, day);
  let base: QuestProgress =
    prev && prev.periodKey === pk ? { ...prev, progress: { ...prev.progress } } : emptyProgress(pk);

  // Once ya completada permanece completada.
  if (def.period === "once" && prev?.completed) {
    return prev;
  }

  for (const goal of def.goals) {
    if (goal.metric === event.type) {
      const inc = goal.useValue ? (event.value ?? 0) : 1;
      base.progress[goal.metric] = (base.progress[goal.metric] ?? 0) + inc;
    }
  }
  base.completed = def.goals.every((g) => (base.progress[g.metric] ?? 0) >= g.target);
  return base;
}
